import { useState, useEffect } from 'react';

export default function ReferralModal({ userId, supabase, userRecord, onClose }) {
  const [referralCode, setReferralCode] = useState('');
  const [copied, setCopied] = useState(false);
  const [personalNote, setPersonalNote] = useState('');
  const [stats, setStats] = useState({ invited: 0, joined: 0, active: 0 });

  useEffect(() => {
    if (!userId || !supabase) return;
    async function loadOrCreateCode() {
      const { data } = await supabase
        .from('users')
        .select('referral_code, invite_count, invite_accepted_count')
        .eq('id', userId)
        .single();
      if (data?.referral_code) {
        setReferralCode(data.referral_code);
        setStats({
          invited: data.invite_count || 0,
          joined: data.invite_accepted_count || 0,
          active: data.invite_accepted_count || 0,
        });
      } else {
        const code = Math.random().toString(36).substring(2, 8).toUpperCase();
        await supabase.from('users').update({ referral_code: code }).eq('id', userId);
        setReferralCode(code);
      }
    }
    loadOrCreateCode();
  }, [userId, supabase]);

  const referralLink = `${typeof window !== 'undefined' ? window.location.origin : 'https://shiftirl.ai'}/join/${referralCode}`;

  async function handleCopy() {
    await navigator.clipboard.writeText(referralLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  async function handleEmail() {
    const subject = encodeURIComponent('I think you should try Shift');
    const body = encodeURIComponent(`${personalNote ? personalNote + '\n\n' : ''}Join me on Shift — an AI that actually gets to know you:\n${referralLink}`);
    window.open(`mailto:?subject=${subject}&body=${body}`);
  }

  async function handleMessage() {
    const text = encodeURIComponent(`${personalNote ? personalNote + ' — ' : ''}Join me on Shift: ${referralLink}`);
    if (navigator.share) {
      navigator.share({ title: 'Join me on Shift', text: personalNote || 'I think you should try this', url: referralLink });
    } else {
      window.open(`sms:?body=${text}`);
    }
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 2000,
      background: 'rgba(0,0,0,0.7)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 20, fontFamily: 'Georgia, serif',
    }}
      onClick={e => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div style={{
        background: '#07070a', border: '1px solid #272736',
        borderRadius: 12, width: '100%', maxWidth: 440, overflow: 'hidden',
      }}>
        <div style={{ padding: '20px 24px 0', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <div>
            <div style={{ color: '#888', fontSize: 16, fontWeight: 'bold', marginBottom: 4 }}>Invite someone to Shift</div>
          </div>
          <div onClick={onClose} style={{ cursor: 'pointer', color: '#333', fontSize: 20, lineHeight: 1, padding: '0 4px' }}
            onMouseEnter={e => e.currentTarget.style.color = '#888'}
            onMouseLeave={e => e.currentTarget.style.color = '#333'}
          >×</div>
        </div>

        <div style={{ padding: '16px 24px 24px', display: 'flex', flexDirection: 'column', gap: 18 }}>
          <div style={{ color: '#444', fontSize: 13, fontStyle: 'italic', lineHeight: 1.7 }}>
            Share your personal link. When they join, Shift remembers you brought them.
          </div>

          <div style={{ background: '#111', border: '1px solid #272736', borderRadius: 8, padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ color: '#555', fontSize: 12, fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
              {referralLink || 'generating...'}
            </span>
            <button
              onClick={handleCopy}
              style={{
                background: 'none', border: `1px solid ${copied ? '#7a9e8233' : '#2a2a2a'}`,
                borderRadius: 6, color: copied ? '#7a9e82' : '#555',
                fontSize: 11, fontFamily: 'Georgia, serif',
                padding: '4px 12px', cursor: 'pointer', flexShrink: 0, whiteSpace: 'nowrap',
              }}
            >
              {copied ? 'Copied ✓' : 'Copy link'}
            </button>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ flex: 1, height: '0.5px', background: '#1e1e2a' }} />
            <span style={{ color: '#2a2a2a', fontSize: 11, fontStyle: 'italic' }}>or share directly</span>
            <div style={{ flex: 1, height: '0.5px', background: '#1e1e2a' }} />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
            {[
              { label: 'Email', color: '#7a9e82', bg: 'rgba(122,158,130,0.1)', onClick: handleEmail,
                icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" stroke="#7a9e82" strokeWidth="1.4"/><path d="M22 6l-10 7L2 6" stroke="#7a9e82" strokeWidth="1.4" strokeLinecap="round"/></svg> },
              { label: 'Message', color: '#9b8fc4', bg: 'rgba(155,143,196,0.1)', onClick: handleMessage,
                icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" stroke="#9b8fc4" strokeWidth="1.4"/></svg> },
              { label: 'Share', color: '#c8a96e', bg: 'rgba(200,169,110,0.1)', onClick: () => { if (navigator.share) navigator.share({ title: 'Join me on Shift', url: referralLink }); else handleCopy(); },
                icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><circle cx="18" cy="5" r="2.5" stroke="#c8a96e" strokeWidth="1.3"/><circle cx="6" cy="12" r="2.5" stroke="#c8a96e" strokeWidth="1.3"/><circle cx="18" cy="19" r="2.5" stroke="#c8a96e" strokeWidth="1.3"/><path d="M8.5 10.5l7-4M8.5 13.5l7 4" stroke="#c8a96e" strokeWidth="1.3" strokeLinecap="round"/></svg> },
            ].map((item, i) => (
              <div key={i} onClick={item.onClick} style={{ background: '#111', border: '1px solid #272736', borderRadius: 8, padding: '10px 8px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, cursor: 'pointer' }}
                onMouseEnter={e => { e.currentTarget.style.background = '#141414'; e.currentTarget.style.borderColor = '#2a2a2a'; }}
                onMouseLeave={e => { e.currentTarget.style.background = '#111'; e.currentTarget.style.borderColor = '#272736'; }}
              >
                <div style={{ width: 28, height: 28, borderRadius: 6, background: item.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {item.icon}
                </div>
                <span style={{ color: '#555', fontSize: 11 }}>{item.label}</span>
              </div>
            ))}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div style={{ color: '#333', fontSize: 11 }}>Add a personal note (optional)</div>
            <textarea
              value={personalNote}
              onChange={e => setPersonalNote(e.target.value)}
              placeholder="Tell them why you're sending this..."
              rows={2}
              style={{ width: '100%', background: '#111', border: '1px solid #272736', borderRadius: 6, color: '#888', fontFamily: 'Georgia, serif', fontSize: 12, padding: '8px 10px', resize: 'none', outline: 'none', lineHeight: 1.6 }}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
            {[
              { num: stats.invited, label: 'invited', color: '#c8a96e' },
              { num: stats.joined, label: 'joined', color: '#7a9e82' },
              { num: stats.active, label: 'active', color: '#9b8fc4' },
            ].map((s, i) => (
              <div key={i} style={{ background: '#111', border: '1px solid #272736', borderRadius: 8, padding: 10, textAlign: 'center' }}>
                <div style={{ fontSize: 18, fontWeight: 'bold', color: s.color, marginBottom: 2 }}>{s.num}</div>
                <div style={{ color: '#333', fontSize: 10, fontStyle: 'italic' }}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
